import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadVigentes as ReadAlertas } from 'services/alerta-cobranca';
import { Read } from 'services/cobranca';
import { normalizeResponse } from 'utils';
import {
  BILLING_QUERIES,
  buildAlertIndex,
  buildIndicators,
  buildRows,
  buildWorstDelay,
  hasCriticalAlert,
} from 'utils/billing';

// Listagem das seis telas do menu Cobranca. As seis batem no mesmo endpoint e
// recebem o mesmo retorno; o que muda e o `query` enviado. Concentrar aqui
// evita que cada tela repita a chamada, o indice de alertas, o mapeamento e o
// calculo dos indicadores.
//
// O recorte da tela vem da QUERY, nao daqui: quem decide o que e Regua,
// Cobranca ou Recuperacao e o endpoint (ver BILLING_QUERIES). O que este hook
// aplica sobre o resultado sao apenas os refinamentos interativos do operador
// — busca global, faixa de/ate e o card de alerta critico.
//
// - `query`       filtros enviados ao endpoint (ver BILLING_QUERIES)
// - `onlyOverdue` recorta as linhas so nas parcelas vencidas
// - `walletView`  visao da Carteira: parcelas em aberto ou baixadas (ver
//                 WALLET_VIEWS; usado quando `onlyOverdue` e false)
// - `refreshMs`   intervalo de recarga; 0 desliga
export default function useBillingList({
  query = BILLING_QUERIES.all,
  onlyOverdue = true,
  walletView = null,
  refreshMs = 120000,
} = {}) {
  const { filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [tick, setTick] = useState(0);
  // Faixa de dias em atraso, preenchida pelo operador no cabecalho.
  const [delayFrom, setDelayFrom] = useState('');
  const [delayTo, setDelayTo] = useState('');
  // Card de alerta critico ligado: mostra so os contratos que ele conta.
  const [criticalOnly, setCriticalOnly] = useState(false);

  useEffect(() => {
    if (!refreshMs) {
      return undefined;
    }
    const interval = setInterval(() => setTick((prev) => prev + 1), refreshMs);
    return () => clearInterval(interval);
  }, [refreshMs]);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      // Os alertas vao junto, mas com catch proprio: falha na marcacao nao
      // pode derrubar a listagem.
      const [result, resultAlertas] = await Promise.all([
        Read(query),
        ReadAlertas().catch((err) => {
          console.error('Erro ao carregar alertas:', err);
          return null;
        }),
      ]);
      setAlertas(normalizeResponse(resultAlertas));
      setRegisters(normalizeResponse(result));
    } catch (err) {
      console.error('Erro ao carregar cobranças:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    init();
  }, [init, tick]);

  const alertIndex = useMemo(() => buildAlertIndex(alertas), [alertas]);

  const rows = useMemo(
    () => buildRows(registers, alertIndex, { onlyOverdue, walletView }),
    [registers, alertIndex, onlyOverdue, walletView]
  );

  const worstDelay = useMemo(() => buildWorstDelay(rows), [rows]);

  // A faixa decide quais CONTRATOS ficam; as parcelas fora do intervalo
  // continuam vindo junto e aparecem ao expandir.
  const visibleRows = useMemo(() => {
    const from = delayFrom === '' ? null : Number(delayFrom);
    const to = delayTo === '' ? null : Number(delayTo);
    const hasRange = Number.isFinite(from) || Number.isFinite(to);

    return (rows || []).filter((row) => {
      if (!filterExpression(row)) {
        return false;
      }
      if (criticalOnly && !hasCriticalAlert(row)) {
        return false;
      }
      if (!hasRange) {
        return true;
      }
      const pior = worstDelay.get(row?.id) || 0;
      if (Number.isFinite(from) && pior < from) {
        return false;
      }
      if (Number.isFinite(to) && pior > to) {
        return false;
      }
      return true;
    });
  }, [rows, worstDelay, filterExpression, criticalOnly, delayFrom, delayTo]);

  const indicators = useMemo(() => buildIndicators(visibleRows), [visibleRows]);

  const clearDelay = useCallback(() => {
    setDelayFrom('');
    setDelayTo('');
  }, []);

  const toggleCritical = useCallback(
    () => setCriticalOnly((atual) => !atual),
    []
  );

  return {
    loading,
    rows,
    visibleRows,
    indicators,
    delayFrom,
    setDelayFrom,
    delayTo,
    setDelayTo,
    clearDelay,
    criticalOnly,
    toggleCritical,
    reload: init,
  };
}
